<template>
    <div class="filhantering-redigera-fil">
        <FilhanteringRedigeraFilForm
            v-if="dataLoaded"
            @closetab="closeTab"
            :show-preview="parameters?.showPreview"
            :tabId="tabId"
        />
    </div>
</template>

<script>
    import BaseTab from "@/tabs/mixins/BaseTab";
    import FilhanteringRedigeraFilForm from "./FilhanteringRedigeraFilForm.vue";
    import { getConfig, klinikenApi } from "@/api";
    import { mapGetters } from "vuex";
    import { computed } from "vue";

    export default {
        extends: BaseTab,
        name: "FilhanteringRedigeraFil",
        tabName: "Redigera fil",
        patientBound: true,
        components: {
            FilhanteringRedigeraFilForm,
        },
        props: {
            parameters: {
                showPreview: {
                    type: Boolean,
                    required: false,
                    default: false,
                },
                // Är filen generell och inte inte kopplad till en patient?
                isGenerell: {
                    type: Boolean,
                    required: false,
                    default: false,
                },
                tabName: {
                    type: String,
                    required: false,
                    default: "Redigera fil",
                },
            },
        },
        data() {
            return {
                dataLoaded: false,
                isGenerellFil: null,
                deleteFile: false,
            };
        },
        provide() {
            return {
                isGenerellFil: computed(() => this.isGenerellFil),
                deleteFile: computed(() => this.deleteFile),
            };
        },
        computed: {
            ...mapGetters("filhantering", ["redigeringFormIsClean"]),
            hasUnsavedData() {
                return !this.redigeringFormIsClean;
            },
        },
        methods: {
            closeTab() {
                this.$store.dispatch("tabs/closeTab", this.tabId);

                // Tömmer fildatan i store
                this.$store.commit("filhantering/reset");
            },

            async loadMetadata({ patientId, fileId }) {
                // Hämta metadata från backend
                const metadataUrl = this.isGenerellFil
                    ? `filhantering/generellafiler/${fileId}/`
                    : `filhantering/${patientId}/fil/${fileId}/`;
                const response = await klinikenApi.get(metadataUrl, getConfig({}, "Filer"));
                const metadata = response.data;

                const backendKeyToFrontend = {
                    id: "fileId",
                    patient: "patientUUID",
                    documentTitle: "kommentar",
                    created_by: "createdBy",
                    created_by_full_name: "createdByFullName",
                    accountableHealthcareProfessional: "accountableHealthcareProfessional",
                    legalAuthenticator: "legalAuthenticator",
                    dokument_typ: "dokumentTyp",
                    ursprungligt_filnamn: "ursprungligtFilnamn",
                    filstorlek: "filstorlek",
                    filformat: "filformat",
                    created_at: "createdAt",
                };
                const backendKeys = [
                    "authorTime",
                    "accountableHealthcareProfessional",
                    "created_by",
                    "created_by_full_name",
                    "documentTime",
                    "documentTitle",
                    "dokument_typ",
                    "id",
                    "legalAuthenticator",
                    "patient",
                    "ursprungligt_filnamn",
                    "filstorlek",
                    "filformat",
                    "created_at",
                ];

                backendKeys.forEach((backendKey) => {
                    const key = backendKeyToFrontend[backendKey];
                    const value = metadata[backendKey];
                    this.$store.commit("filhantering/redigeringAngeMetaData", { key, value });
                });

                // dokumentTime -> dokumentDatum
                if (metadata.documentTime) {
                    const value = metadata.documentTime.substring(0, 10);
                    this.$store.commit("filhantering/redigeringAngeMetaData", { key: "dokumentDatum", value });
                }

                // authorTime -> inmatningsDatum
                if (metadata.authorTime || metadata.created_at) {
                    const value = metadata.authorTime ?? metadata.created_at;
                    this.$store.commit("filhantering/redigeringAngeMetaData", {
                        key: "inmatningsDatum",
                        value: value?.substring(0, 10),
                    });
                }

                if (!this.isGenerellFil) {
                    // Hämta patient data from backend
                    const patientUrl = `core/patienter/${patientId}/`;
                    const patientResponse = await klinikenApi.get(patientUrl, getConfig());

                    const patientKeys = ["personId", "personIdType", "fornamn", "efternamn", "namn"];

                    patientKeys.forEach((key) => {
                        this.$store.commit("filhantering/redigeringAngeMetaData", {
                            key,
                            value: patientResponse.data[key],
                        });
                    });
                }

                // Förevisa komponenten
                this.dataLoaded = true;
            },
            async loadData(data) {
                // Töm eventuell kvarliggande fildata i store
                this.$store.commit("filhantering/reset");

                this.dataLoaded = false;
                await this.loadMetadata({
                    patientId: data.patient,
                    fileId: data.id,
                });
                this.$store.commit("filhantering/redigeringSetFormClean");
            },
        },

        async created() {
            this.isGenerellFil = this.parameters.isGenerell;
            this.deleteFile = this.parameters.deleteFile;

            if (this.parameters) {
                // Tabben ska inte vara patientbound om filen inte är kopplad till en patient
                if (this.isGenerellFil && !this.parameters.patient) {
                    this.patientBound = false;
                }

                await this.loadData(this.parameters);
            }

            // Listen for events from FilhanteringFiler telling us
            // that we need to display another file
            this.$root.$on("file-in-editing-tab-exchanged", (data) => {
                this.loadData(data.parameters);
            });
        },
    };
</script>
